import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'

// ═══════════════════════════════════════════════════════════════
// SPX CONFLUENCE INDICATOR — SHARED LOGIC
// ═══════════════════════════════════════════════════════════════
const C = {
  bg:     '#0a0a0c',
  bg2:    '#111115',
  bg3:    '#16161a',
  text:   '#e8e6e0',
  muted:  '#6b6960',
  dim:    '#2e2e32',
  accent: '#00d4ff',
  bull:   '#00c48c',
  bear:   '#ff4d6a',
  warn:   '#f0c040',
  border: '#1e1e22',
};

function calcEMA(values, period) {
  if (!values || values.length === 0) return null;
  if (values.length < period) return values.reduce((a, b) => a + b, 0) / values.length;
  const k = 2 / (period + 1);
  let ema = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < values.length; i++) ema = values[i] * k + ema * (1 - k);
  return ema;
}

function calcRSI(closes, period = 14) {
  if (!closes || closes.length < period + 1) return null;
  const slice = closes.slice(-(period + 1));
  let gains = 0, losses = 0;
  for (let i = 1; i < slice.length; i++) {
    const diff = slice[i] - slice[i - 1];
    if (diff > 0) gains += diff; else losses -= diff;
  }
  const avgGain = gains / period;
  const avgLoss = losses / period;
  if (avgLoss === 0) return 100;
  return 100 - 100 / (1 + avgGain / avgLoss);
}

function calcATR(candles, period = 14) {
  if (!candles || candles.length < period + 1) return null;
  const slice = candles.slice(-(period + 1));
  const trs = slice.slice(1).map((c, i) => {
    const prev = slice[i].close;
    return Math.max(c.high - c.low, Math.abs(c.high - prev), Math.abs(c.low - prev));
  });
  return trs.reduce((a, b) => a + b, 0) / period;
}

function calcMACD(closes) {
  if (!closes || closes.length < 26) return null;
  const ema12 = calcEMA(closes.slice(-30), 12);
  const ema26 = calcEMA(closes, 26);
  if (ema12 === null || ema26 === null) return null;
  return { macd: ema12 - ema26 };
}

function useSignals(candles, price, vwap) {
  return useMemo(() => {
    if (!candles || candles.length < 10 || !price) return null;
    const closes  = candles.map(c => c.close);
    const volumes = candles.map(c => c.vol || 0);

    const ema9  = calcEMA(closes.slice(-20), 9);
    const ema20 = calcEMA(closes.slice(-35), 20);
    const ema50 = calcEMA(closes.slice(-70), 50);
    const rsi   = calcRSI(closes);
    const macdData = calcMACD(closes);
    const atr   = calcATR(candles);

    const avgVol = volumes.slice(-20).reduce((a, b) => a + b, 0) / 20 || 1;
    const lastVol = volumes[volumes.length - 1] || 0;
    const volAboveAvg = lastVol > avgVol * 1.1;

    const aboveVwap = vwap > 0 ? price > vwap : true;
    const ema9Above20  = ema9 !== null && ema20 !== null && ema9 > ema20;
    const ema20Above50 = ema20 !== null && ema50 !== null && ema20 > ema50;
    const macdBull = macdData && macdData.macd > 0;

    let rsiBull = false;
    if (rsi !== null) rsiBull = rsi > 50 && rsi < 75;

    let score = 0;
    if (aboveVwap)   score += 2;   else score -= 2;
    if (rsiBull)     score += 1.5; else if (rsi !== null) score -= 1.5;
    if (macdBull)    score += 1.5; else score -= 1.5;
    if (ema9Above20) score += 1.5; else score -= 1.5;
    if (ema20Above50) score += 1;  else score -= 1;
    if (volAboveAvg)  score += 0.5; else score -= 0.5;
    score = Math.max(-10, Math.min(10, Math.round(score * 10) / 10));

    const bias = score >= 2 ? 'LONG' : score <= -2 ? 'SHORT' : 'NEUTRAL';

    const signals = {
      vwap:    { bull: aboveVwap,    label: 'VWAP',     value: vwap > 0 ? (aboveVwap ? `Above ${vwap.toFixed(1)}` : `Below ${vwap.toFixed(1)}`) : 'No VWAP' },
      rsi:     { bull: rsiBull,      label: 'RSI(14)',   value: rsi !== null ? rsi.toFixed(1) : '—' },
      macd:    { bull: macdBull,     label: 'MACD',      value: macdData ? (macdBull ? 'Bullish' : 'Bearish') : '—' },
      ema9_20: { bull: ema9Above20,  label: 'EMA 9/20',  value: ema9Above20 ? 'Bullish cross' : 'Bearish cross' },
      ema20_50:{ bull: ema20Above50, label: 'EMA 20/50', value: ema20Above50 ? 'Trend confirmed' : 'Trend weak' },
      volume:  { bull: volAboveAvg,  label: 'Volume',    value: volAboveAvg ? 'Above avg' : 'Below avg' },
    };

    return { score, bias, signals, rsi, atr, ema9, ema20, ema50 };
  }, [candles, price, vwap]);
}

function MiniChart({ candles, price, vwap }) {
  const W = 600, H = 160, PAD = { t: 10, b: 10, l: 6, r: 6 };
  if (!candles || candles.length < 2) return null;

  const slice = candles.slice(-60);
  const closes  = slice.map(c => c.close);
  const highs   = slice.map(c => c.high);
  const lows    = slice.map(c => c.low);
  const vwaps   = slice.map(c => c.vwap).filter(Boolean);

  const ema9Line  = slice.map((_, i) => calcEMA(closes.slice(0, i + 1), 9));
  const ema20Line = slice.map((_, i) => calcEMA(closes.slice(0, i + 1), 20));
  const ema50Line = slice.map((_, i) => calcEMA(closes.slice(0, i + 1), 50));

  const allVals = [...closes, ...highs, ...lows, ...vwaps,
    ...ema9Line.filter(Boolean), ...ema20Line.filter(Boolean), ...ema50Line.filter(Boolean)];
  const minP = Math.min(...allVals);
  const maxP = Math.max(...allVals);
  const range = maxP - minP || 1;

  const xOf = (i) => PAD.l + (i / (slice.length - 1)) * (W - PAD.l - PAD.r);
  const yOf = (v) => PAD.t + (1 - (v - minP) / range) * (H - PAD.t - PAD.b);

  const barW = Math.max(2, (W - PAD.l - PAD.r) / slice.length - 1);

  const polyline = (vals, color, sw, dash = '') =>
    <polyline key={color + sw} points={vals.map((v, i) => v !== null ? `${xOf(i)},${yOf(v)}` : null).filter(Boolean).join(' ')} fill="none" stroke={color} strokeWidth={sw} strokeDasharray={dash} opacity={0.85} />;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* Candlestick bars */}
        {slice.map((c, i) => {
          const x = xOf(i);
          const bull = c.close >= c.open;
          const bodyTop    = yOf(Math.max(c.open, c.close));
          const bodyBot    = yOf(Math.min(c.open, c.close));
          const bodyH      = Math.max(1, bodyBot - bodyTop);
          const col        = bull ? C.bull : C.bear;
          return (
            <g key={i}>
              <line x1={x} y1={yOf(c.high)} x2={x} y2={yOf(c.low)} stroke={col} strokeWidth={0.8} opacity={0.6} />
              <rect x={x - barW / 2} y={bodyTop} width={barW} height={bodyH} fill={col} opacity={0.75} />
            </g>
          );
        })}
        {/* VWAP */}
        {vwaps.length > 1 && polyline(slice.map(c => c.vwap || null), '#8b8b6e', 1.2, '4 2')}
        {/* EMAs */}
        {polyline(ema50Line, C.muted,  0.8)}
        {polyline(ema20Line, C.warn,   1.0)}
        {polyline(ema9Line,  C.accent, 1.2)}
      </svg>
      <div style={{ display: 'flex', gap: '14px', padding: '4px 6px', fontSize: '9px', fontFamily: 'monospace', color: C.muted, flexWrap: 'wrap' }}>
        <span style={{ color: C.accent }}>── EMA9</span>
        <span style={{ color: C.warn   }}>── EMA20</span>
        <span style={{ color: C.muted  }}>── EMA50</span>
        <span style={{ color: '#8b8b6e' }}>··· VWAP</span>
      </div>
    </div>
  );
}

function GaugeMeter({ score }) {
  const pct  = (score + 10) / 20;
  const col  = score >= 2 ? C.bull : score <= -2 ? C.bear : C.warn;
  const W = 300, H = 24;
  const fillW = pct * W;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '10px', fontFamily: 'monospace', color: C.muted }}>
        <span style={{ color: C.bear }}>SHORT −10</span>
        <span style={{ color: C.warn }}>NEUTRAL 0</span>
        <span style={{ color: C.bull }}>LONG +10</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <rect x={0} y={4} width={W} height={H - 8} rx={4} fill={C.bg3} />
        <rect x={0} y={4} width={Math.max(0, fillW)} height={H - 8} rx={4} fill={col} opacity={0.8} />
        <line x1={W / 2} y1={0} x2={W / 2} y2={H} stroke={C.muted} strokeWidth={1} opacity={0.4} />
        <rect x={Math.max(0, fillW) - 1.5} y={1} width={3} height={H - 2} rx={1.5} fill={col} />
      </svg>
      <div style={{ textAlign: 'center', marginTop: '6px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: 700, color: col }}>{score > 0 ? '+' : ''}{score.toFixed(1)}</span>
        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.muted, marginLeft: '8px' }}>/ 10</span>
      </div>
    </div>
  );
}

function SignalRow({ signal, label, value }) {
  const col = signal.bull ? C.bull : C.bear;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderBottom: `0.5px solid ${C.dim}`, fontSize: '11px', fontFamily: 'monospace' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: col, fontSize: '8px' }}>{'⬤'}</span>
        <span style={{ color: C.text }}>{signal.label}</span>
      </div>
      <span style={{ color: col, fontSize: '11px' }}>{signal.value}</span>
    </div>
  );
}

// ── LIVE data hook (real Tradier data) ──────────────────────────
function useSPXLiveFeed() {
  const [price, setPrice]               = useState(null);
  const [candles, setCandles]           = useState([]);
  const [vwap, setVwap]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [candleStatus, setCandleStatus] = useState(null);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [prevClose, setPrevClose]       = useState(null);
  const [levels, setLevels]             = useState(null);
  const [marketsClosed, setMarketsClosed] = useState(false);

  const ts = () => new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const calcLevels = (cds, cur) => {
    if (!cds.length) return null;
    const todayHigh  = Math.max(...cds.map(c => c.high));
    const todayLow   = Math.min(...cds.map(c => c.low));
    const openPrice  = cds[0].open;
    const snap = (v, step) => Math.round(v / step) * step;
    return {
      todayHigh,
      todayLow,
      openPrice,
      round1Above: snap(cur + 25, 25),
      round2Above: snap(cur + 50, 25),
      round1Below: snap(cur - 25, 25),
      round2Below: snap(cur - 50, 25),
    };
  };

  async function fetchQuote() {
    try {
      const params = new URLSearchParams({ endpoint: 'markets/quotes', symbols: 'SPX' });
      const res  = await fetch(`/api/tradier?${params}`);
      const data = await res.json();
      if (data.quotes?.quote?.last) {
        const last = parseFloat(data.quotes.quote.last);
        setPrice(last);
        setLastUpdate(ts());
        setLoading(false);
        setError(null);
        return last;
      }
      return null;
    } catch (err) {
      console.error('Quote error:', err);
      return null;
    }
  }

  async function fetchCandles() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const params = new URLSearchParams({
        endpoint:       'markets/timesales',
        symbol:         'SPX',
        interval:       '5min',
        start:          `${today} 09:30`,
        end:            `${today} 16:00`,
        session_filter: 'open',
      });
      const url = `/api/tradier?${params}`;
      const res  = await fetch(url);
      const data = await res.json();

      let series = null;
      if (data?.series?.data) {
        series = Array.isArray(data.series.data) ? data.series.data : [data.series.data];
      }

      if (!series || series.length === 0) {
        setCandleStatus('empty');
        const q = await fetchQuote();
        if (!q) setError('Last known price shown. Markets open Mon-Fri 9:30 AM - 4:00 PM ET');
        setLoading(false);
        return;
      }

      let cumVol = 0, cumPV = 0;
      const processed = series.map(c => {
        const vol = parseFloat(c.volume) || 1;
        cumVol += vol;
        const typical = (parseFloat(c.high) + parseFloat(c.low) + parseFloat(c.close)) / 3;
        cumPV += typical * vol;
        return {
          time:  c.timestamp ? c.timestamp * 1000 : new Date(c.time).getTime(),
          open:  parseFloat(c.open),
          high:  parseFloat(c.high),
          low:   parseFloat(c.low),
          close: parseFloat(c.close),
          vol,
          vwap:  cumVol > 0 ? cumPV / cumVol : parseFloat(c.close),
        };
      });

      if (processed.length > 0) {
        setCandles(processed);
        setCandleStatus('ok');
        const last = processed[processed.length - 1];
        setPrice(last.close);
        setVwap(last.vwap);
        setLevels(calcLevels(processed, last.close));
        setLastUpdate(ts());
        setLoading(false);
        setError(null);
      } else {
        setCandleStatus('empty');
        const q = await fetchQuote();
        if (!q) setError('Last known price shown. Markets open Mon-Fri 9:30 AM - 4:00 PM ET');
        setLoading(false);
      }
    } catch (err) {
      console.error('Candles error:', err);
      setCandleStatus('error');
      const q = await fetchQuote();
      if (!q) setError('Unable to fetch live data.');
      setLoading(false);
    }
  }

  async function fetchPrevClose() {
    try {
      const end   = new Date().toISOString().split('T')[0];
      const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const res  = await fetch('/api/tradier?' + new URLSearchParams({
        endpoint: 'markets/history',
        symbol:   'SPX',
        interval: 'daily',
        start,
        end,
      }));
      const data = await res.json();
      if (data.history?.day) {
        const days = Array.isArray(data.history.day) ? data.history.day : [data.history.day];
        const prev = days[days.length - 2] || days[days.length - 1];
        if (prev?.close) setPrevClose(parseFloat(prev.close));
      }
    } catch {}
  }

  function isMarketOpen() {
    const now = new Date();
    const etTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const day = etTime.getDay();
    const timeInMinutes = etTime.getHours() * 60 + etTime.getMinutes();
    const isWeekday = day >= 1 && day <= 5;
    const marketOpen  = 9 * 60 + 30;
    const marketClose = 16 * 60;
    return isWeekday && timeInMinutes >= marketOpen && timeInMinutes < marketClose;
  }

  useEffect(() => {
    const marketOpen = isMarketOpen();
    if (!marketOpen) setMarketsClosed(true);

    fetchQuote();

    if (marketOpen) {
      fetchCandles();
      const t1 = setInterval(fetchCandles, 2 * 60 * 1000);
      const t2 = setInterval(fetchQuote,   30 * 1000);
      fetchPrevClose();
      return () => { clearInterval(t1); clearInterval(t2); };
    } else {
      fetchPrevClose();
      const t2 = setInterval(fetchQuote, 5 * 60 * 1000);
      return () => clearInterval(t2);
    }
  }, []); // eslint-disable-line

  return { price, candles, vwap, loading, error, candleStatus, lastUpdate, prevClose, levels, marketsClosed, fetchQuote, fetchCandles };
}

// ── Price Ladder ────────────────────────────────────────────────
function PriceLadder({ price, vwap, levels, prevClose }) {
  if (!levels || !price) return null;
  const { todayHigh, todayLow, openPrice, round1Above, round2Above, round1Below, round2Below } = levels;

  const allLevels = [
    { price: todayHigh,   label: "Today's High",    type: 'resistance', key: 'high' },
    { price: round2Above, label: `${round2Above}`,  type: 'round',      key: 'r2a'  },
    { price: round1Above, label: `${round1Above}`,  type: 'round',      key: 'r1a'  },
    vwap      && { price: vwap,      label: 'VWAP',          type: 'vwap',       key: 'vwap' },
    openPrice && { price: openPrice, label: "Today's Open",  type: 'open',       key: 'open' },
    prevClose && { price: prevClose, label: 'Prev Close',    type: 'prevclose',  key: 'prev' },
    { price: round1Below, label: `${round1Below}`,  type: 'round',      key: 'r1b'  },
    { price: round2Below, label: `${round2Below}`,  type: 'round',      key: 'r2b'  },
    { price: todayLow,    label: "Today's Low",     type: 'support',    key: 'low'  },
  ].filter(Boolean).sort((a, b) => b.price - a.price);

  const deduped = allLevels.filter((l, i) =>
    i === 0 || Math.abs(l.price - allLevels[i - 1].price) > 1
  );

  const insertAt = deduped.findIndex(l => l.price < price);
  const rows = insertAt === -1
    ? [...deduped, { price, label: 'CURRENT', type: 'current', key: 'cur' }]
    : [
        ...deduped.slice(0, insertAt),
        { price, label: 'CURRENT', type: 'current', key: 'cur' },
        ...deduped.slice(insertAt),
      ];

  const typeColor = {
    resistance: C.bear,
    support:    C.bull,
    vwap:       C.accent,
    open:       C.warn,
    prevclose:  C.warn,
    round:      C.muted,
    current:    C.accent,
  };

  return (
    <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden', marginBottom: '14px' }}>
      <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.12em', padding: '10px 12px 8px' }}>PRICE LADDER</div>
      <div style={{ paddingBottom: '8px' }}>
        {rows.map(row => {
          const col = typeColor[row.type] || C.muted;
          const isCurrent = row.type === 'current';
          const dist = ((row.price - price) / price * 100);
          return (
            <div key={row.key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: isCurrent ? '7px 12px' : '5px 12px',
              background: isCurrent ? `${C.accent}12` : 'transparent',
              borderLeft: isCurrent ? `2px solid ${C.accent}` : `2px solid transparent`,
              borderBottom: isCurrent ? `0.5px solid ${C.accent}30` : 'none',
              borderTop: isCurrent ? `0.5px solid ${C.accent}30` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isCurrent && <span style={{ color: C.accent, fontSize: '8px' }}>▶</span>}
                <span style={{ fontFamily: 'monospace', fontSize: isCurrent ? '10px' : '10px', color: isCurrent ? C.accent : col, fontWeight: isCurrent ? 700 : 400, letterSpacing: '0.04em' }}>
                  {isCurrent ? 'CURRENT' : row.label}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                {!isCurrent && (
                  <span style={{ fontFamily: 'monospace', fontSize: '9px', color: C.dim }}>
                    {dist > 0 ? `+${dist.toFixed(2)}%` : `${dist.toFixed(2)}%`}
                  </span>
                )}
                <span style={{ fontFamily: 'monospace', fontSize: isCurrent ? '13px' : '11px', fontWeight: isCurrent ? 700 : 500, color: col }}>
                  {row.price.toFixed(2)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Entry Zone ──────────────────────────────────────────────────
function EntryZone({ price, bias, atr, levels, vwap, score }) {
  const [showHelp, setShowHelp] = useState(false);

  const EZ_MUTED = 'rgba(226,232,240,0.7)';
  const EZ_SUB   = 'rgba(226,232,240,0.5)';

  if (!price || !bias || !levels) return null;

  if (Math.abs(score) < 3) return (
    <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '9px', color: EZ_MUTED, letterSpacing: '0.12em' }}>ENTRY ZONE</div>
        <button onClick={() => setShowHelp(s => !s)} style={{ background: 'transparent', border: 'none', color: C.accent, fontSize: '10px', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.06em' }}>
          {showHelp ? '▲ HIDE' : '? HOW TO USE'}
        </button>
      </div>
      {showHelp && <HelpContent onHide={() => setShowHelp(false)} />}
      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.warn, marginBottom: '4px' }}>MIXED SIGNALS</div>
      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: EZ_MUTED, lineHeight: 1.7 }}>Confluence score too low for a defined entry. Wait for clearer signal.</div>
    </div>
  );

  const atrVal = atr && atr > 0 ? atr : 15;
  const biasColor = bias === 'LONG' ? C.bull : C.bear;

  let entryLow, entryHigh, stopLevel, target1, target2;
  if (bias === 'LONG') {
    const anchor = Math.min(vwap || price, price - atrVal * 0.3);
    entryLow  = anchor   - atrVal * 0.2;
    entryHigh = anchor   + atrVal * 0.2;
    stopLevel = entryLow - atrVal * 0.5;
    target1   = entryHigh + atrVal * 1.0;
    target2   = entryHigh + atrVal * 2.0;
  } else {
    const anchor = Math.max(vwap || price, price + atrVal * 0.3);
    entryLow  = anchor    - atrVal * 0.2;
    entryHigh = anchor    + atrVal * 0.2;
    stopLevel = entryHigh + atrVal * 0.5;
    target1   = entryLow  - atrVal * 1.0;
    target2   = entryLow  - atrVal * 2.0;
  }

  const riskPts   = Math.abs(bias === 'LONG' ? entryLow - stopLevel : stopLevel - entryHigh);
  const rewardPts = Math.abs(bias === 'LONG' ? target1 - entryHigh  : entryLow - target1);
  const rrRatio   = riskPts > 0 ? (rewardPts / riskPts).toFixed(1) : '—';
  const rrNum     = parseFloat(rrRatio) || 0;
  const barTotal  = riskPts + rewardPts;
  const riskPct   = barTotal > 0 ? (riskPts / barTotal * 100).toFixed(0) : 50;
  const rewPct    = barTotal > 0 ? (rewardPts / barTotal * 100).toFixed(0) : 50;

  const row = (label, val, color, sub) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: `0.5px solid ${C.dim}` }}>
      <div>
        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: EZ_MUTED }}>{label}</div>
        {sub && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: EZ_SUB, marginTop: '3px' }}>{sub}</div>}
      </div>
      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color }}>{val}</span>
    </div>
  );

  return (
    <div style={{ background: C.bg2, border: `0.5px solid ${biasColor}40`, borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontSize: '9px', color: EZ_MUTED, letterSpacing: '0.12em' }}>ENTRY ZONE</div>
          <span style={{ background: `${biasColor}18`, border: `1px solid ${biasColor}50`, borderRadius: '4px', padding: '3px 10px', fontSize: '10px', fontWeight: 700, color: biasColor, letterSpacing: '0.1em' }}>{bias} SETUP</span>
        </div>
        <button onClick={() => setShowHelp(s => !s)} style={{ background: 'transparent', border: 'none', color: C.accent, fontSize: '10px', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
          {showHelp ? '▲ HIDE' : '? HOW TO USE'}
        </button>
      </div>

      {showHelp && <HelpContent onHide={() => setShowHelp(false)} />}

      {row('Entry Zone', `${entryLow.toFixed(2)} – ${entryHigh.toFixed(2)}`, biasColor, 'Watch for price to enter this range')}
      {row('Stop Loss',  stopLevel.toFixed(2), C.bear, 'Exit if price closes beyond this level')}
      {row('Target 1',  target1.toFixed(2),   C.bull, `First profit target (${rrRatio}:1 R/R)`)}
      {row('Target 2',  target2.toFixed(2),   `${C.bull}88`, 'Extended target if momentum continues')}

      <div style={{ marginTop: '12px' }}>
        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: EZ_MUTED, letterSpacing: '0.1em', marginBottom: '6px' }}>RISK / REWARD</div>
        <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${riskPct}%`, background: C.bear, opacity: 0.7 }} />
          <div style={{ width: `${rewPct}%`, background: C.bull, opacity: 0.7 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.bear }}>Risk {riskPts.toFixed(1)} pts</span>
          <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: rrNum >= 2 ? C.bull : rrNum >= 1 ? C.warn : C.bear }}>{rrRatio}:1</span>
          <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.bull }}>Reward {rewardPts.toFixed(1)} pts</span>
        </div>
      </div>

      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: EZ_SUB, fontStyle: 'italic', lineHeight: 1.7, marginTop: '10px', paddingTop: '10px', borderTop: `0.5px solid ${C.dim}` }}>
        Entry zones are calculated from ATR and VWAP. Always confirm with your TOS charts before entering. Not financial advice.
      </div>
    </div>
  );
}

function HelpContent({ onHide }) {
  const EZ_BODY = 'rgba(226,232,240,0.8)';
  const section = (title, text) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: C.accent, letterSpacing: '0.08em', fontWeight: 700, marginBottom: '5px' }}>{title}</div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', color: EZ_BODY, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{text}</div>
    </div>
  );

  return (
    <div style={{ background: 'rgba(0,212,255,0.05)', border: `0.5px solid rgba(0,212,255,0.2)`, borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: C.accent, letterSpacing: '0.1em', fontWeight: 700, marginBottom: '14px' }}>HOW TO USE THE ENTRY ZONE</div>

      {section('ENTRY ZONE',
`The price range where you watch for a setup.

For LONG: wait for price to pull back INTO the entry zone and show a bullish candle (close above open, green body) before entering.

For SHORT: wait for price to bounce UP INTO the entry zone and show a bearish candle (close below open, red body) before entering.

Do not chase price — wait for it to come to you.`)}

      {section('STOP LOSS',
`The price level where you exit if wrong.

Place your stop BEYOND this level. If price closes past the stop on a 5-minute candle — exit. No exceptions.

This is calculated at 0.5x ATR beyond the entry zone edge.`)}

      {section('TARGETS',
`TARGET 1: take partial profits here. This is 1x ATR from your entry. Consider closing 50–75% of your position at Target 1.

TARGET 2: let the remainder run. This is 2x ATR from entry. Only reached on strong trending days.`)}

      {section('RISK/REWARD RATIO',
`Shows how much you can make vs how much you risk.

2:1 means for every $1 risked you can make $2.

Only take trades where R/R is at least 1.5:1 or better.`)}

      {section('WHEN NOT TO TRADE',
`— Score below 3 (mixed signals)
— Within 30 min of open (9:30–10:00 AM ET)
— Within 30 min of close (3:30–4:00 PM ET)
— During major news events
— When ATR is unusually high (more than 2x average)`)}

      <button onClick={onHide} style={{ background: 'transparent', border: 'none', color: C.accent, fontSize: '10px', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.06em', paddingLeft: 0, marginTop: '4px' }}>
        ▲ HIDE
      </button>
    </div>
  );
}

// ── SPX CONFLUENCE LIVE (admin only — real Tradier data) ────────
function SPXConfluenceLive() {
  const { price, candles, vwap, loading, error, candleStatus, lastUpdate, prevClose, levels, marketsClosed, fetchQuote, fetchCandles } = useSPXLiveFeed();
  const result = useSignals(candles, price || 0, vwap || 0);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCandles();
    await fetchQuote();
    setRefreshing(false);
  };

  const refreshBtn = (
    <button
      onClick={handleRefresh}
      disabled={refreshing}
      style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '6px', padding: '6px 12px', color: '#c9a84c', fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", cursor: refreshing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: refreshing ? 0.6 : 1, marginTop: '6px' }}>
      {refreshing ? '↻ Refreshing...' : '↻ Refresh'}
    </button>
  );

  const fontLink = (
    <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap" rel="stylesheet" />
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono',monospace" }}>
      {fontLink}
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: C.accent, fontSize: '14px', marginBottom: '8px', letterSpacing: '0.1em' }}>LOADING LIVE SPX DATA...</div>
        <div style={{ color: C.muted, fontSize: '10px', letterSpacing: '0.08em' }}>Connecting to market feed</div>
      </div>
    </div>
  );

  if (error && !price) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono',monospace" }}>
      {fontLink}
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ color: C.warn, fontSize: '12px', marginBottom: '10px', letterSpacing: '0.06em', maxWidth: '340px', lineHeight: 1.7 }}>{error}</div>
        <div style={{ color: C.muted, fontSize: '10px', letterSpacing: '0.06em' }}>Markets open Mon–Fri 9:30 AM – 4:00 PM ET</div>
      </div>
    </div>
  );

  if (!result && price) return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'JetBrains Mono',monospace", color: C.text, padding: '20px 16px', maxWidth: '780px', margin: '0 auto' }}>
      {fontLink}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.12em', color: C.accent }}>SPX CONFLUENCE</div>
          <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.1em', marginTop: '2px' }}>{marketsClosed ? 'LAST KNOWN PRICE' : 'LIVE · 5-MIN CANDLES'}</div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div style={{ fontSize: '26px', fontWeight: 700, color: C.text, lineHeight: 1 }}>{price.toFixed(2)}</div>
          {lastUpdate && <div style={{ fontSize: '9px', color: C.dim, marginTop: '4px' }}>Updated {lastUpdate}</div>}
          {marketsClosed
            ? <div style={{ display: 'inline-block', marginTop: '4px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '20px', padding: '2px 10px', fontSize: '9px', color: '#c9a84c', letterSpacing: '0.1em' }}>● MARKETS CLOSED</div>
            : <div style={{ display: 'inline-block', marginTop: '4px', background: 'rgba(0,196,140,0.12)', border: '1px solid rgba(0,196,140,0.3)', borderRadius: '20px', padding: '2px 10px', fontSize: '9px', color: C.bull, letterSpacing: '0.1em' }}>● LIVE</div>}
          {refreshBtn}
        </div>
      </div>
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
        <div style={{ color: C.accent, fontSize: '11px', letterSpacing: '0.1em', marginBottom: '6px' }}>PRICE FEED ACTIVE</div>
        <div style={{ color: C.muted, fontSize: '10px', letterSpacing: '0.06em', lineHeight: 1.8 }}>
          {candleStatus === 'empty'
            ? 'Candle data loading — waiting for 5-min bars to accumulate after market open.'
            : 'Awaiting candle data...'}
        </div>
      </div>
    </div>
  );

  const { score, bias, signals, atr } = result;
  const biasColor = bias === 'LONG' ? C.bull : bias === 'SHORT' ? C.bear : C.warn;
  const highConf = Math.abs(score) >= 6;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'JetBrains Mono',monospace", color: C.text, padding: '20px 16px', maxWidth: '780px', margin: '0 auto' }}>
      {fontLink}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.12em', color: C.accent }}>SPX CONFLUENCE</div>
          <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.1em', marginTop: '2px' }}>{marketsClosed ? 'LAST KNOWN PRICE' : 'LIVE · 5-MIN CANDLES'}</div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div style={{ fontSize: '26px', fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {price.toFixed(2)}
          </div>
          {vwap > 0 && <div style={{ fontSize: '10px', color: C.muted, marginTop: '2px' }}>VWAP {vwap.toFixed(2)}</div>}
          {lastUpdate && <div style={{ fontSize: '9px', color: C.dim, marginTop: '2px' }}>Updated {lastUpdate}</div>}
          {marketsClosed
            ? <div style={{ display: 'inline-block', marginTop: '4px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '20px', padding: '2px 10px', fontSize: '9px', color: '#c9a84c', letterSpacing: '0.1em' }}>● MARKETS CLOSED</div>
            : <div style={{ display: 'inline-block', marginTop: '4px', background: 'rgba(0,196,140,0.12)', border: '1px solid rgba(0,196,140,0.3)', borderRadius: '20px', padding: '2px 10px', fontSize: '9px', color: C.bull, letterSpacing: '0.1em' }}>● LIVE</div>}
          {refreshBtn}
        </div>
      </div>

      {/* Mindset card */}
      <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
        <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', color: 'rgba(226,232,240,0.6)', lineHeight: 1.8, fontStyle: 'italic', margin: 0 }}>
          This gives you something objective to hold onto when the urge to enter is louder than the signals.<br /><br />
          When the score says NEUTRAL — that is your permission to wait. The market will always give you another opportunity. The best trade is sometimes no trade.
        </p>
      </div>

      {/* Chart */}
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
        <MiniChart candles={candles} price={price} vwap={vwap} />
      </div>

      {/* Gauge + Bias */}
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.12em', marginBottom: '10px' }}>CONFLUENCE SCORE</div>
        <GaugeMeter score={score} />
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <span style={{ background: `${biasColor}18`, border: `1px solid ${biasColor}50`, borderRadius: '6px', padding: '6px 20px', fontSize: '14px', fontWeight: 700, color: biasColor, letterSpacing: '0.12em' }}>{bias}</span>
        </div>
      </div>

      {/* High confluence alert */}
      {highConf && (
        <div style={{ background: `${biasColor}10`, border: `1px solid ${biasColor}40`, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '11px', color: biasColor, letterSpacing: '0.06em' }}>
          ⬤ HIGH CONFLUENCE — {bias === 'LONG' ? 'Strong bullish alignment across indicators' : 'Strong bearish alignment across indicators'}
        </div>
      )}

      {/* Price Ladder */}
      <PriceLadder price={price} vwap={vwap} levels={levels} prevClose={prevClose} />

      {/* Entry Zone */}
      <EntryZone price={price} bias={bias} atr={atr} levels={levels} vwap={vwap} score={score} />

      {/* Signal breakdown */}
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden', marginBottom: '14px' }}>
        <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.12em', padding: '10px 12px 6px' }}>SIGNAL BREAKDOWN</div>
        {Object.entries(signals).map(([key, sig]) => <SignalRow key={key} signal={sig} />)}
      </div>

      {/* Footer */}
      <div style={{ fontSize: '9px', color: C.dim, textAlign: 'center', letterSpacing: '0.08em', paddingTop: '8px' }}>
        LIVE SPX DATA VIA TRADIER · 5-MIN CANDLES · NOT FINANCIAL ADVICE
      </div>
    </div>
  );
}

export { C, useSignals, MiniChart, GaugeMeter, SignalRow };
export default SPXConfluenceLive;
